// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import DatePicker from 'react-datepicker';

import Select2, { DataSelect2 } from 'wdna-select2';

import './react-datepicker.css';

import { LovDO } from '../../../../configuration/requests';
import { SensorListDO, WasteInfoDO, WasteContainerDO } from '../../../requests';

interface Props {
  formValues: {
    isNew: boolean;
    dtype?: WasteContainerDO;
    data?: Partial<WasteContainerDO>;
    dinfo?: WasteInfoDO;
    info?: Partial<WasteInfoDO>;
    sensorArray?: SensorListDO[];
    modelWasteArray?: LovDO[];
    typeWasteArray?: LovDO[];
    wasteInfoTable?: WasteInfoDO[];
  };
  duplicate: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null) => void;
  onChangeInfo: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAutoFormMin: (data: WasteContainerDO) => number;
  onAutoFormMax: (data: WasteContainerDO) => number;
}

/**
 * Function to generate a form component
 *
 * @param props properties of the component
 * @returns jsx element with the form
 */
function Form(props: Props): JSX.Element {
  const { sensorArray, modelWasteArray, typeWasteArray, isNew } = props.formValues;
  const data: WasteContainerDO = (props.formValues.dtype ? props.formValues.dtype : props.formValues.data) as WasteContainerDO;
  const info: WasteInfoDO = (props.formValues.dinfo ? props.formValues.dinfo : props.formValues.info) as WasteInfoDO;

  const timezoneOffset: any = {
    hours: Math.trunc(-data.date.getTimezoneOffset() / 60),
    minutes: -data.date.getTimezoneOffset() % 60,
  };
  // eslint-disable-next-line max-len
  const timezoneString: string = `UTC ${timezoneOffset.hours < 0 ? '-' : '+'}${timezoneOffset.hours.toString().padStart(2, '0')}:${timezoneOffset.minutes.toString().padStart(2, '0')}`;

  return (
    <fieldset>
      <div className="row">
        {sensorArray && (
          <div className="col-6">
            <label className="label">
              <b>Sensor</b> &nbsp;
            </label>
            <Select2
              name="id"
              disabled={!isNew}
              data={sensorArray.map((sensor: SensorListDO): DataSelect2 => {
                return { text: `[${sensor.code}] ${sensor.name}`, id: sensor.id };
              })}
              value={data.id || ''}
              options={{
                placeholder: 'Select option',
              }}
              onChange={props.onChange}
              style={{ width: '80%' }}
            />
            <label>&nbsp;&nbsp;*</label>
          </div>
        )}
        <div className="col-3">
          <label className="label">
            <b>Type</b> &nbsp;
          </label>
          <Select2
            name="type"
            data={typeWasteArray?.map((item: LovDO): DataSelect2 => {
              return { id: item.id, text: item.name };
            })}
            value={data.type || ''}
            options={{
              placeholder: 'Select option',
            }}
            onChange={props.onChange}
            style={{ width: '80%' }}
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
        <div className="col-3">
          <label className="label">
            <b>Model</b> &nbsp;
          </label>
          <Select2
            name="model"
            data={modelWasteArray?.map((item: LovDO): DataSelect2 => {
              return { id: item.id, text: item.name };
            })}
            value={data.model || ''}
            options={{
              placeholder: 'Select option',
            }}
            onChange={props.onChange}
            style={{ width: '80%' }}
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MIN Unload</b> &nbsp;&nbsp;
          </label>
          <input
            className="inputNew"
            type="number"
            min="0"
            value={data.minUnload || props.onAutoFormMin(data)}
            name="minUnload"
            onChange={props.onChange}
            required
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MAX Weight</b> &nbsp;&nbsp;
          </label>
          <input
            className="inputNew"
            type="number"
            min="0"
            value={data.maxWeight || props.onAutoFormMax(data)}
            name="maxWeight"
            onChange={props.onChange}
            required
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MIN Temp</b> &nbsp;&nbsp;
          </label>
          <input
            className="inputNew"
            type="number"
            min="0"
            value={data.minTemp || '1'}
            name="minTemp"
            onChange={props.onChange}
            required
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MAX Temp</b> &nbsp;&nbsp;
          </label>
          <input
            className="inputNew"
            type="number"
            min="0"
            value={data.maxTemp || '50'}
            name="maxTemp"
            onChange={props.onChange}
            required
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
      </div>
      <br />
      {(isNew || props.duplicate) && (
        <div id="info">
          <div className="row">
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-1</b>
              </label>
              <input
                className="inputNew"
                type="number"
                value={info.ratio1 || 0}
                name="ratio1"
                onChange={props.onChangeInfo}
                required
              />
              <label>&nbsp;&nbsp;*</label>
            </div>
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-2</b>
              </label>
              <input
                className="inputNew"
                type="number"
                value={info.ratio2 || 0}
                name="ratio2"
                onChange={props.onChangeInfo}
                required
              />
              <label>&nbsp;&nbsp;*</label>
            </div>
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-3</b>
              </label>
              <input
                className="inputNew"
                type="number"
                value={info.ratio3 || 0}
                name="ratio3"
                onChange={props.onChangeInfo}
                required
              />
              <label>&nbsp;&nbsp;*</label>
            </div>
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-4</b>
              </label>
              <input
                className="inputNew"
                type="number"
                value={info.ratio4 || 0}
                name="ratio4"
                onChange={props.onChangeInfo}
                required
              />
              <label>&nbsp;&nbsp;*</label>
            </div>
          </div>
          <br />
        </div>
      )}
      <div className="row">
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>Date of creation</b> &nbsp;
          </label>
          <i className="fa fa-calendar">
            &nbsp;
          </i>
          <DatePicker
            selected={data.date}
            onChange={props.onChange}
            className="inputNew"
            name="date"
            showTimeSelect
            timeFormat="HH:mm:ss"
            timeIntervals={15}
            dateFormat={`yyyy-MM-dd HH:mm:ss '${timezoneString}'`}
            timeCaption="time"
            todayButton="TODAY"
            disabled={!isNew}
          />
          <label>&nbsp;&nbsp;{isNew ? '*' : ''}</label>
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>Status</b>&nbsp;
          </label>
          <input
            className="inputNew"
            type="checkbox"
            name="active"
            data-handler-type="boolean"
            checked={data.active || false}
            onChange={props.onChange}
          />
          &nbsp;&nbsp;
          <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            Active
          </label>
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>Size</b> &nbsp;&nbsp;
          </label>
          <input
            className="inputNew"
            type="number"
            min="0"
            value={data.size || ''}
            name="size"
            onChange={props.onChange}
            required
          />
          <label>&nbsp;&nbsp;*</label>
        </div>
      </div>
    </fieldset>
  );
}

export default Form;
